import { getRegionTagLabel } from '../../../utils/facets/region-tag-labels';
import { formatFilterLabel } from '../../../utils/facets/format-filter-label';

export interface LocationSegmentValue {
  /** The first applied location/remoteness value's display label, or null when nothing is
   * applied (the caller falls back to the plain "Location" word). */
  text: string | null;
  /** How many *additional* location values are applied beyond the first one (0 when there's
   * only one, or when `text` came from remoteness rather than a location facet). */
  extraCount: number;
}

/**
 * Resolves what the fused search bar's location segment should show when a location or
 * remoteness filter is applied (Phase 1 review fix F3): the first applied location value's
 * display label (plus how many more are applied), falling back to the remoteness label when
 * only remoteness is set, or null when nothing is applied at all (caller shows "Location").
 *
 * Under the TEC-482 new location system, `regionTags` values are gazetteer codes and need
 * `getRegionTagLabel` to become readable ("658225" -> "Helsinki"); the old flat `location`
 * facet's values are already display text. This resolves them exactly the way
 * components/navigation/menu/MenuContinueSection.tsx resolves a recent search chip's place name,
 * reusing the same helper rather than duplicating the regionTags -> label mapping.
 */
export function resolveLocationSegmentValue(
  filtersObj: Record<string, string[] | undefined> | null | undefined,
  locale: 'en' | 'fi' | 'sv',
  useRegionTags: boolean,
): LocationSegmentValue {
  const locationAttribute = useRegionTags ? 'regionTags' : 'location';
  const locationValues = filtersObj?.[locationAttribute] ?? [];
  if (locationValues.length > 0) {
    const [first] = locationValues;
    const text = useRegionTags ? getRegionTagLabel(first, locale) : first;
    return { text, extraCount: locationValues.length - 1 };
  }
  const remotenessValues = filtersObj?.remoteness ?? [];
  if (remotenessValues.length > 0) {
    return { text: formatFilterLabel('remoteness', remotenessValues[0]), extraCount: 0 };
  }
  return { text: null, extraCount: 0 };
}

/** Formats resolveLocationSegmentValue's result into the final display string, e.g.
 * "Helsinki +2", or null when nothing is applied (caller falls back to "Location"). */
export function formatLocationSegmentLabel(value: LocationSegmentValue): string | null {
  if (!value.text) return null;
  return value.extraCount > 0 ? `${value.text} +${value.extraCount}` : value.text;
}
